import React from 'react';

class AboutMain extends React.Component {
    render() {
        return (
            <div className="container">
                <div className="row">
                    <div className="col-md-8">
                        <h2>
                            <span className="acronym-letter">S</span>ystematic{' '}
                            <span className="acronym-letter">E</span>valuation of the{' '}
                            <span className="acronym-letter">A</span>pplication of{' '}
                            <span className="acronym-letter">Z</span>ebrafish in{' '}
                            <span className="acronym-letter">T</span>oxicology -{' '}
                            <span className="acronym-letter">D</span>ata{' '}
                            <span className="acronym-letter">I</span>ntegration and{' '}
                            <span className="acronym-letter">V</span>isualization{' '}
                            <span className="acronym-letter">E</span>nabling{' '}
                            <span className="acronym-letter">R</span>esource (
                            <span className="acronym-letter">SEAZIT-DIVER</span>)
                        </h2>
                        <br />
                        <img
                            className="img-responsive center-block"
                            src="/static_seazit/img/seazit/logo-2.jpg"
                            alt="Logo: Cartoon schematic of zebrafish development"
                        />
                        <br />

                        <p>
                            The small size and rapid development of the zebrafish (Danio rerio)
                            embryo make it a useful vertebrate model for assessing the potential
                            effects of test substances on growth and development (i.e.,
                            developmental toxicity) using high-throughput screening methods.
                            However, deficits in the following key areas hinder the broader adoption
                            of the embryonic zebrafish model for toxicological screening:
                        </p>

                        <ul className="ps-4">
                            <li>Consistent experimental protocol elements</li>
                            <li>
                                Consistent informatics approaches used for classification of
                                outcomes
                            </li>
                            <li>
                                Clear understanding of mechanisms of chemical absorption,
                                distribution, metabolism, and excretion
                            </li>
                        </ul>

                        <p>
                            To facilitate broader acceptance and increase confidence for
                            toxicological screening, the Division of Translational Toxicology
                            (formerly Division of the National Toxicology Program, DNTP) at NIEHS
                            established the Systematic Evaluation of the Application of Zebrafish in
                            Toxicology (<a href="https://ntp.niehs.nih.gov/go/seazit">SEAZIT</a>)
                            program, jointly led by DTT scientists and those in the NTP Interagency
                            Center for the Evaluation of Alternative Toxicological Methods
                            (NICEATM). Information gathering sessions and workshops hosted by SEAZIT
                            led to the development of several projects to specifically address the
                            impact of protocol design and informatics approaches used for
                            classification of toxicity outcomes.
                        </p>

                        <h2>Interlaboratory Study:</h2>
                        <p>
                            Participating laboratories utilized in-house protocols for toxicological
                            screening in zebrafish embryos to evaluate a defined set of test
                            substances while varying the two protocol elements which are anticipated
                            to influence toxicity outcomes (
                            <a href="https://www.altex.org/index.php/altex/article/view/997">
                                Hamm et al 2019
                            </a>
                            ):
                        </p>
                        <ul className="ps-4">
                            <li>
                                Intact chorion vs embryo dechorionation prior to exposure to a test
                                substance
                            </li>
                            <li>
                                Static (i.e., one-time) vs static-renewal exposure (i.e., daily)
                                over the 5-day study period
                            </li>
                        </ul>
                        <p>
                            Test substances were selected based upon 1) overlap with other DTT
                            studies, 2) available reference data from rodent and other zebrafish
                            developmental toxicity studies representing a range of potencies, and 3)
                            physicochemical properties. In total, 41 test substances were blinded
                            and provided to three laboratories for assessment in
                            concentration-response in triplicate. Three of the 41 test substances
                            are duplicates, which were randomly selected to assess assay
                            reproducibility within a single experiment. Additionally, DMSO and
                            3,4-Dichloroanaline were provided to laboratories for evaluation as
                            vehicle and positive controls, respectively, within and across
                            experimental runs. For more details regarding substance information,
                            please refer to the Test Substances tab in the Dataset page on this
                            website.
                        </p>

                        <p>
                            Toxicity endpoints evaluated include mortality and the development of
                            altered phenotypes (i.e., malformations). A dose-range finding (DRF)
                            study was conducted to establish a concentration range to optimally
                            evaluate potency shifts across experimental conditions. In the DRF, each
                            laboratory tested all test substances under one assay condition prior to
                            conducting the Definitive (Def) Study which included a comparative
                            assessment of all test substances under 4 assay conditions (e.g., static
                            exposure with intact chorion; static exposure following dechorionation;
                            static-renewal exposure with intact chorion; static-renewal exposure
                            following dechorionation).
                        </p>

                        <div className="text-center d-flex flex-column align-items-center">
                            <img
                                className="img-fluid mx-auto"
                                style={{ width: '800px', height: 'auto' }}
                                src="/static_seazit/img/seazit/logo-4.png"
                                alt="Figure 1"
                            />
                            <p className="fw-bold mt-3">Figure 1</p>
                        </div>

                        <p>
                            Figure 1: Schematic representation of the Interlaboratory Study design.
                            Laboratories participating in the study exposed embryos under four
                            exposure conditions including static exposure, renewal of exposure media
                            every 24 hours, using both chorionated (ON) and dechorionated (OFF)
                            embryos. For more details regarding lab specific protocols, please refer
                            to the Protocol tab in the Dataset page on this website.
                        </p>

                        <p>
                            To compare results across laboratories, the incidence of mortality
                            and/or altered phenotypes was collected for each embryo. Due to a lack
                            of common terminology describing chemical-induced phenotypes across the
                            laboratories, an additional step of ontology harmonization was applied
                            before data analysis. For more information regarding ontology mapping,
                            please refer to the Phenotype Ontology Tab in the Datasets page of this
                            website. All data and the associated ontology terms are stored in the
                            open-source PostgreSQL database. By leveraging the database, we
                            implemented a data analysis pipeline to derive a benchmark concentration
                            (BMC) value for each test substance and endpoint under investigation (
                            <a href="https://www.mdpi.com/2305-6304/11/5/407">Hsieh et al 2023</a>).
                        </p>

                        <p>
                            SEAZIT-DIVER was developed to analyze, compare, and visualize intra- and
                            inter-laboratory data in an interactive web-application. Within
                            SEAZIT-DIVER, comparisons are organized on different tabs including
                            experimental design summary, quality control, chemical-specific
                            concentration response curves, ranking of chemical toxicity per
                            lab/assay, and comparison of results across assays and laboratories.
                        </p>

                        <h2>Suggestions?</h2>
                        <p>
                            We are currently refining assays, analyses, and visualizations, and
                            would appreciate your input! Please direct questions or suggestions to
                            Kristen Ryan (
                            <a href="mailto:kristen.ryan@nih.gov">kristen.ryan@nih.gov</a>).
                        </p>
                        <p>Current version: SEAZIT-DIVER (2026-06-16).</p>
                    </div>
                </div>
            </div>
        );
    }
}

export default AboutMain;
